import type { SceneObject } from "./SceneHost";
import {
  pathLinecap,
  pathLinejoin,
  pathStrokeColor,
  pathStrokeDash,
  pathStrokeWidth,
  applyStrokeDashAttr,
  readObjectStyle,
} from "../lib/board/sceneStyle";
import { svgGroupTransform } from "./transform/rotate";

export type PathPoint = [number, number];

export type PathBounds = { x: number; y: number; w: number; h: number };

const SVG_NS = "http://www.w3.org/2000/svg";

export function isPathObject(obj: SceneObject): obj is SceneObject & { points: PathPoint[] } {
  return obj.type === "path" && Array.isArray(obj.points) && obj.points.length >= 2;
}

export function pathStrokeWidthFromObject(obj: SceneObject): number {
  return pathStrokeWidth(obj);
}

export function computePathBBox(points: PathPoint[], strokeWidth = 3): PathBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [px, py] of points) {
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }
  const pad = Math.max(1, strokeWidth / 2 + 1);
  const w = Math.max(1, maxX - minX + pad * 2);
  const h = Math.max(1, maxY - minY + pad * 2);
  return {
    x: minX - pad,
    y: minY - pad,
    w,
    h,
  };
}

export function pathLength(points: PathPoint[]): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i]![0] - points[i - 1]![0];
    const dy = points[i]![1] - points[i - 1]![1];
    len += Math.hypot(dx, dy);
  }
  return len;
}

function pointDistance(a: PathPoint, b: PathPoint): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Прореживание соседних точек (как thinPathPoints в старой доске). */
export function thinPathPoints(points: PathPoint[], minDist = 0.32): PathPoint[] {
  if (points.length < 3) return clonePoints(points);
  const out: PathPoint[] = [points[0]!];
  let lx = points[0]![0];
  let ly = points[0]![1];
  const md2 = minDist * minDist;
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i]!;
    const dx = p[0] - lx;
    const dy = p[1] - ly;
    if (dx * dx + dy * dy >= md2) {
      out.push(p);
      lx = p[0];
      ly = p[1];
    }
  }
  out.push(points[points.length - 1]!);
  return out.length >= 2 ? out : clonePoints(points);
}

/**
 * Точность хранения координат штриха: 2 знака после запятой.
 * Округление до целых заметно «комкает» рукописную линию — при копировании
 * и дублировании штрих должен сохранять ту же точность, что и оригинал.
 */
export function roundPathCoord(n: number): number {
  return Math.round(n * 100) / 100;
}

function roundCoord(n: number): number {
  return roundPathCoord(n);
}

/**
 * Компактное хранение без заметной «доводки» после отпускания.
 */
/**
 * Допуск упрощения штриха при сохранении, в единицах сцены.
 *
 * Сглаживание (Чайкин + оконное) добавляет точки обратно после прореживания,
 * поэтому в хранилище их оказывалось втрое больше нужного: замер на живой
 * доске — 141 штрих, 22 тысячи точек, в среднем 157 на штрих, и 90% веса
 * сцены. Отсюда и упирание в лимит размера, и тяжёлая перерисовка при зуме.
 *
 * 0.12 выбрано по этому же замеру: убирает около 60% точек, а линия
 * отклоняется меньше чем на 0.5 экранного пикселя даже при увеличении 4x —
 * то есть глазом разницы нет, а сцена становится вдвое легче.
 */
export const STORAGE_SIMPLIFY_EPSILON = 0.12;

export function compactStrokePointsForStorage(points: PathPoint[]): PathPoint[] {
  if (points.length < 2) return clonePoints(points);
  let pts = thinPathPoints(points, 0.22);
  if (pts.length < 2) pts = clonePoints(points);
  // Раньше упрощение включалось только для штрихов длиннее 1000 точек,
  // а типичный штрих — 157, поэтому оно не срабатывало почти никогда.
  const simplified = simplifyPolyline(pts, STORAGE_SIMPLIFY_EPSILON);
  if (simplified.length >= 2) pts = simplified;
  if (pts.length > 1000) {
    pts = simplifyPolyline(pts, 0.35);
  }
  return pts.map(([x, y]) => [roundCoord(x), roundCoord(y)] as PathPoint);
}

export function simplifyPathPoints(points: PathPoint[], minDist: number): PathPoint[] {
  if (points.length <= 2) return clonePoints(points);
  const simplified: PathPoint[] = [points[0]!];
  for (let i = 1; i < points.length - 1; i++) {
    if (pointDistance(points[i]!, simplified[simplified.length - 1]!) >= minDist) {
      simplified.push(points[i]!);
    }
  }
  simplified.push(points[points.length - 1]!);
  return simplified;
}

/** Chaikin: срезает углы — меньше «огрызков», ближе к Miro. */
export function chaikinCornerCut(points: PathPoint[], iterations = 2): PathPoint[] {
  if (points.length < 3 || iterations < 1) return clonePoints(points);
  let pts = clonePoints(points);
  for (let iter = 0; iter < iterations; iter++) {
    if (pts.length < 3) break;
    const next: PathPoint[] = [pts[0]!];
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i]!;
      const p1 = pts[i + 1]!;
      next.push([0.75 * p0[0] + 0.25 * p1[0], 0.75 * p0[1] + 0.25 * p1[1]]);
      next.push([0.25 * p0[0] + 0.75 * p1[0], 0.25 * p0[1] + 0.75 * p1[1]]);
    }
    next[0] = pts[0]!;
    next[next.length - 1] = pts[pts.length - 1]!;
    pts = next;
  }
  return pts;
}

function movingAverageSmooth(points: PathPoint[], radius = 1): PathPoint[] {
  if (points.length <= 2 || radius < 1) return clonePoints(points);
  const out: PathPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    if (i === 0 || i === points.length - 1) {
      out.push(points[i]!);
      continue;
    }
    let sx = 0;
    let sy = 0;
    let n = 0;
    const a = Math.max(0, i - radius);
    const b = Math.min(points.length - 1, i + radius);
    for (let j = a; j <= b; j++) {
      sx += points[j]![0];
      sy += points[j]![1];
      n += 1;
    }
    out.push([sx / n, sy / n]);
  }
  return out;
}

/**
 * Раньше здесь был moving-average по всему штриху на каждом кадре —
 * из-за этого уже нарисованный хвост «плыл» и линия дёргалась.
 * Оставляем точки как есть: сглаживание даёт только Bezier в buildStrokePathD.
 */
export function subtleBlendStrokePoints(points: PathPoint[], _tipKeep = 0): PathPoint[] {
  return clonePoints(points);
}

export function subtleFinalizeStrokePoints(points: PathPoint[]): PathPoint[] {
  return clonePoints(points);
}

export function prepareStrokePoints(points: PathPoint[], _zoom = 1): PathPoint[] {
  return clonePoints(points);
}

/** @deprecated alias */
export function smoothPathPoints(points: PathPoint[], zoom = 1): PathPoint[] {
  return prepareStrokePoints(points, zoom);
}

function penDotStrokeEpsilonWorld(zoom: number): number {
  return Math.max(1e-6, 0.06 / (zoom > 0 ? zoom : 1));
}

/**
 * Лёгкое скругление осевшей части штриха (края линии без «шаварушек»).
 * Кончик (последние tipKeep точек) не трогаем — без змейки/резинки.
 */
export function softenStrokeCorners(
  points: PathPoint[],
  amount = 0.3,
  passes = 1,
  tipKeep = 3
): PathPoint[] {
  if (points.length < 4 || amount <= 0 || passes <= 0) return clonePoints(points);
  const keep = Math.max(1, tipKeep);
  let cur: PathPoint[] = points;
  for (let pass = 0; pass < passes; pass++) {
    const out = clonePoints(cur);
    const lastSoft = cur.length - keep;
    for (let i = 1; i < lastSoft; i++) {
      const prev = cur[i - 1]!;
      const p = cur[i]!;
      const next = cur[i + 1]!;
      out[i] = [
        p[0] * (1 - amount) + ((prev[0] + next[0]) / 2) * amount,
        p[1] * (1 - amount) + ((prev[1] + next[1]) / 2) * amount,
      ];
    }
    cur = out;
  }
  return cur === points ? clonePoints(points) : cur;
}

/**
 * Окно по соседям для внутренних точек — убивает зигзаг медленной дрожи.
 * Кончик и старт не трогаем.
 */
export function windowSmoothInterior(points: PathPoint[], halfWindow: number): PathPoint[] {
  if (points.length < 5 || halfWindow <= 0) return clonePoints(points);
  const out = clonePoints(points);
  const last = points.length - 1;
  for (let i = 1; i < last; i++) {
    const a = Math.max(0, i - halfWindow);
    // не усредняем с кончиком — иначе «резинка» у курсора
    const b = Math.min(last - 1, i + halfWindow);
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let j = a; j <= b; j++) {
      sx += points[j]![0];
      sy += points[j]![1];
      n += 1;
    }
    out[i] = [sx / n, sy / n];
  }
  return out;
}

/**
 * Catmull–Rom → cubic Bezier: кривая проходит через точки,
 * углы мышиного следа не превращаются в «зубья».
 * На последнем сегменте — без экстраполяции за конец (иначе «мини-хвостик»).
 */
export function buildStrokePathD(
  points: PathPoint[],
  originX: number,
  originY: number,
  zoom = 1
): string {
  if (!points.length) return "";
  if (points.length === 1) {
    const p0 = points[0]!;
    const he = penDotStrokeEpsilonWorld(zoom) / 2;
    return `M ${p0[0] - originX - he} ${p0[1] - originY} L ${p0[0] - originX + he} ${p0[1] - originY}`;
  }
  if (points.length === 2) {
    return `M ${points[0]![0] - originX} ${points[0]![1] - originY} L ${points[1]![0] - originX} ${points[1]![1] - originY}`;
  }

  const ox = originX;
  const oy = originY;
  const n = points.length;
  let d = `M ${points[0]![0] - ox} ${points[0]![1] - oy}`;

  for (let i = 0; i < n - 1; i++) {
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    let c1x: number;
    let c1y: number;
    let c2x: number;
    let c2y: number;
    if (i === n - 2) {
      // Последний сегмент: контрольные точки только вдоль хорды p1→p2.
      // Catmull с p0 «сбоку» легко выносит дугу за конец штриха.
      c1x = p1[0] + (p2[0] - p1[0]) / 3;
      c1y = p1[1] + (p2[1] - p1[1]) / 3;
      c2x = p2[0] - (p2[0] - p1[0]) / 3;
      c2y = p2[1] - (p2[1] - p1[1]) / 3;
    } else {
      const p0 = points[i === 0 ? 0 : i - 1]!;
      const p3 = points[i + 2]!;
      // tension ~0.5 (делим на 6) — мягко, без петель на острых углах
      c1x = p1[0] + (p2[0] - p0[0]) / 6;
      c1y = p1[1] + (p2[1] - p0[1]) / 6;
      c2x = p2[0] - (p3[0] - p1[0]) / 6;
      c2y = p2[1] - (p3[1] - p1[1]) / 6;
    }
    d += ` C ${c1x - ox} ${c1y - oy} ${c2x - ox} ${c2y - oy} ${p2[0] - ox} ${p2[1] - oy}`;
  }
  return d;
}

/** Живой след пера: ломаная по сырым точкам. */
export function buildPolylineD(
  points: PathPoint[],
  originX: number,
  originY: number,
  zoom = 1
): string {
  if (!points.length) return "";
  if (points.length === 1) {
    const p0 = points[0]!;
    const he = penDotStrokeEpsilonWorld(zoom) / 2;
    return `M ${p0[0] - originX - he} ${p0[1] - originY} L ${p0[0] - originX + he} ${p0[1] - originY}`;
  }
  let d = `M ${points[0]![0] - originX} ${points[0]![1] - originY}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i]![0] - originX} ${points[i]![1] - originY}`;
  }
  return d;
}

/** Пока пишешь: mid-point Bezier по уже положенным точкам (без перетаскивания хвоста). */
export function buildLivePathD(
  points: PathPoint[],
  originX: number,
  originY: number,
  zoom = 1
): string {
  return buildStrokePathD(points, originX, originY, zoom);
}

/** После отпускания — тот же гладкий путь. */
export function buildPathD(
  points: PathPoint[],
  originX: number,
  originY: number,
  zoom = 1
): string {
  return buildStrokePathD(points, originX, originY, zoom);
}

/**
 * Убирает короткий обратный «крючок» на конце (отскок пальца/мыши при отпускании).
 * Не трогает остальной штрих.
 */
export function trimStrokeTipHook(points: PathPoint[], maxHookLen: number): PathPoint[] {
  if (points.length < 3 || maxHookLen <= 0) return clonePoints(points);
  const out = clonePoints(points);
  while (out.length >= 3) {
    const a = out[out.length - 3]!;
    const b = out[out.length - 2]!;
    const c = out[out.length - 1]!;
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const bcx = c[0] - b[0];
    const bcy = c[1] - b[1];
    const abLen = Math.hypot(abx, aby);
    const bcLen = Math.hypot(bcx, bcy);
    if (bcLen > maxHookLen || bcLen < 1e-8 || abLen < 1e-8) break;
    const dot = (abx * bcx + aby * bcy) / (abLen * bcLen);
    // Продолжение вперёд — оставляем; резкий разворот — срезаем кончик.
    if (dot > 0.2) break;
    out.pop();
  }
  return out.length >= 2 ? out : clonePoints(points);
}

export function clonePoints(points: PathPoint[]): PathPoint[] {
  return points.map((p) => [p[0], p[1]] as PathPoint);
}

export function translatePathPoints(points: PathPoint[], dx: number, dy: number): PathPoint[] {
  return points.map(([px, py]) => [px + dx, py + dy]);
}

export function scalePathPointsInGroup(
  points: PathPoint[],
  groupStart: PathBounds,
  groupNext: PathBounds
): PathPoint[] {
  const sx = groupStart.w > 0 ? groupNext.w / groupStart.w : 1;
  const sy = groupStart.h > 0 ? groupNext.h / groupStart.h : 1;
  return points.map(([px, py]) => [
    groupNext.x + (px - groupStart.x) * sx,
    groupNext.y + (py - groupStart.y) * sy,
  ]);
}

export function applyPathGeometry(
  obj: SceneObject & { points: PathPoint[] },
  points: PathPoint[]
): void {
  const bbox = computePathBBox(points, pathStrokeWidth(obj));
  obj.points = points;
  obj.x = bbox.x;
  obj.y = bbox.y;
  obj.w = bbox.w;
  obj.h = bbox.h;
}

export function simplifyPolyline(points: PathPoint[], epsilon = 1.5): PathPoint[] {
  if (points.length <= 2) return clonePoints(points);

  function perpendicularDistance(p: PathPoint, a: PathPoint, b: PathPoint): number {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    if (dx === 0 && dy === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy);
    const projX = a[0] + t * dx;
    const projY = a[1] + t * dy;
    return Math.hypot(p[0] - projX, p[1] - projY);
  }

  function rdp(pts: PathPoint[]): PathPoint[] {
    if (pts.length < 3) return pts;
    let maxDist = 0;
    let index = 0;
    const end = pts.length - 1;
    for (let i = 1; i < end; i++) {
      const d = perpendicularDistance(pts[i]!, pts[0]!, pts[end]!);
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }
    if (maxDist > epsilon) {
      const left = rdp(pts.slice(0, index + 1));
      const right = rdp(pts.slice(index));
      return [...left.slice(0, -1), ...right];
    }
    return [pts[0]!, pts[end]!];
  }

  return rdp(points);
}

export function syncPathOnGroup(g: SVGGElement, obj: SceneObject): void {
  if (!isPathObject(obj)) return;
  const strokeWidth = pathStrokeWidth(obj);
  const bbox = computePathBBox(obj.points, strokeWidth);
  obj.x = bbox.x;
  obj.y = bbox.y;
  obj.w = bbox.w;
  obj.h = bbox.h;

  g.setAttribute("transform", svgGroupTransform(obj));

  let pathEl = g.querySelector("path");
  if (!pathEl) {
    pathEl = document.createElementNS(SVG_NS, "path");
    pathEl.setAttribute("fill", "none");
    pathEl.setAttribute("pointer-events", "visibleStroke");
    g.appendChild(pathEl);
  }

  pathEl.setAttribute("d", buildPathD(obj.points, obj.x, obj.y));
  pathEl.setAttribute("fill", "none");
  pathEl.setAttribute("stroke", pathStrokeColor(obj));
  pathEl.setAttribute("stroke-width", String(strokeWidth));
  pathEl.setAttribute("stroke-linecap", pathLinecap(obj));
  pathEl.setAttribute("stroke-linejoin", pathLinejoin(obj));
  pathEl.setAttribute("pointer-events", "visibleStroke");
  applyStrokeDashAttr(pathEl, pathStrokeDash(obj), strokeWidth);
  const strokeOpacity = readObjectStyle(obj).strokeOpacity;
  if (typeof strokeOpacity === "number") {
    pathEl.setAttribute("stroke-opacity", String(strokeOpacity));
    pathEl.removeAttribute("fill-opacity");
  } else {
    pathEl.removeAttribute("stroke-opacity");
    pathEl.removeAttribute("fill-opacity");
  }
}

export function syncPathElement(el: HTMLElement | SVGGElement, obj: SceneObject): void {
  if (el instanceof SVGGElement) {
    syncPathOnGroup(el, obj);
    return;
  }
  if (!isPathObject(obj)) return;
  const strokeWidth = pathStrokeWidth(obj);
  const bbox = computePathBBox(obj.points, strokeWidth);
  obj.x = bbox.x;
  obj.y = bbox.y;
  obj.w = bbox.w;
  obj.h = bbox.h;

  el.style.left = `${obj.x}px`;
  el.style.top = `${obj.y}px`;
  el.style.width = `${obj.w}px`;
  el.style.height = `${obj.h}px`;

  let svg = el.querySelector("svg");
  if (!svg) {
    svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    svg.setAttribute("fill", "none");
    const pathEl = document.createElementNS(SVG_NS, "path");
    pathEl.setAttribute("fill", "none");
    svg.appendChild(pathEl);
    el.appendChild(svg);
  }

  svg.setAttribute("viewBox", `0 0 ${obj.w} ${obj.h}`);
  const pathEl = svg.querySelector("path");
  if (!pathEl) return;

  pathEl.setAttribute("d", buildPathD(obj.points, obj.x, obj.y));
  pathEl.setAttribute("fill", "none");
  pathEl.setAttribute("stroke", pathStrokeColor(obj));
  pathEl.setAttribute("stroke-width", String(strokeWidth));
  pathEl.setAttribute("stroke-linecap", pathLinecap(obj));
  pathEl.setAttribute("stroke-linejoin", pathLinejoin(obj));
  applyStrokeDashAttr(pathEl, pathStrokeDash(obj), strokeWidth);
  const strokeOpacity = readObjectStyle(obj).strokeOpacity;
  if (typeof strokeOpacity === "number") {
    pathEl.setAttribute("stroke-opacity", String(strokeOpacity));
  } else {
    pathEl.removeAttribute("stroke-opacity");
  }
  pathEl.removeAttribute("fill-opacity");
}

export function createPathPreviewSvg(): {
  root: SVGSVGElement;
  path: SVGPathElement;
  setPoints: (
    points: PathPoint[],
    stroke: string,
    strokeWidth: number,
    linecap: string,
    strokeDash?: string,
    pathMode?: "curve" | "polyline"
  ) => void;
} {
  const root = document.createElementNS(SVG_NS, "svg");
  root.setAttribute("class", "wb-pen-preview");
  root.style.position = "absolute";
  root.style.left = "0";
  root.style.top = "0";
  root.style.width = "1px";
  root.style.height = "1px";
  root.style.overflow = "visible";
  root.style.pointerEvents = "none";

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "none");
  root.appendChild(path);

  let boxX = 0;
  let boxY = 0;
  let boxW = 1;
  let boxH = 1;
  let hasBox = false;

  return {
    root,
    path,
    setPoints(points, stroke, strokeWidth, linecap, strokeDash = "solid", pathMode = "polyline") {
      if (points.length < 2) {
        path.setAttribute("d", "");
        hasBox = false;
        return;
      }
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const [px, py] of points) {
        minX = Math.min(minX, px);
        minY = Math.min(minY, py);
        maxX = Math.max(maxX, px);
        maxY = Math.max(maxY, py);
      }
      const pad = Math.max(2, strokeWidth / 2 + 2);
      const nextX = Math.floor(minX - pad);
      const nextY = Math.floor(minY - pad);
      const nextR = Math.ceil(maxX + pad);
      const nextB = Math.ceil(maxY + pad);

      if (!hasBox) {
        boxX = nextX;
        boxY = nextY;
        boxW = Math.max(1, nextR - nextX);
        boxH = Math.max(1, nextB - nextY);
        hasBox = true;
      } else {
        const right = Math.max(boxX + boxW, nextR);
        const bottom = Math.max(boxY + boxH, nextB);
        boxX = Math.min(boxX, nextX);
        boxY = Math.min(boxY, nextY);
        boxW = Math.max(1, right - boxX);
        boxH = Math.max(1, bottom - boxY);
      }

      root.style.left = `${boxX}px`;
      root.style.top = `${boxY}px`;
      root.style.width = `${boxW}px`;
      root.style.height = `${boxH}px`;
      root.setAttribute("viewBox", `0 0 ${boxW} ${boxH}`);
      path.setAttribute(
        "d",
        pathMode === "curve"
          ? buildStrokePathD(points, boxX, boxY)
          : buildPolylineD(points, boxX, boxY)
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", stroke);
      path.setAttribute("stroke-width", String(strokeWidth));
      path.setAttribute("stroke-linecap", linecap);
      path.setAttribute("stroke-linejoin", "round");
      applyStrokeDashAttr(
        path,
        strokeDash as "solid" | "dashed" | "dotted" | undefined,
        strokeWidth
      );
    },
  };
}
